import { Expose, Type } from 'class-transformer';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, of } from 'rxjs';
import { map, share, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { SnUser, SnUserSettings } from '@lib/api/entities';
import { SnUsersAPI } from '@lib/api/services';
import { FsDataDogRumService } from '@lib/data-dog-rum';

@Injectable({ providedIn: 'root' })
export class SnCurrentUser extends SnUser implements OnDestroy {
  @Expose()
  @Type(() => SnUserSettings)
  public settings: SnUserSettings;

  @Expose()
  public isSupportUser: boolean;

  private readonly _settings$ = new Subject<SnUserSettings>();
  private readonly _updated$ = new Subject<boolean>();
  private readonly _callSign$ = new Subject<string>();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _toastrService: ToastrService,
    private readonly _dataDogRum: FsDataDogRumService,
  ) {
    super();
    this._initSettingsListener();
  }

  private _initSettingsListener(): void {
    this._settings$.subscribe((settings) => {
      const res = settings?.authTokens?.axonconnect;
      if (res) {
        const obtainedInMilliseconds = res.obtained ? new Date(res.obtained).getTime() : 0;
        this._dataDogRum.setGlobalContextProperty(
          'axon_access_token_obtained_ms',
          obtainedInMilliseconds,
        );
        this._dataDogRum.setGlobalContextProperty(
          'axon_access_token_expiry_ms',
          obtainedInMilliseconds + res.expiresIn * 1000,
        );
        this._dataDogRum.setGlobalContextProperty(
          'axon_refresh_token_obtained_ms',
          obtainedInMilliseconds,
        );
        this._dataDogRum.setGlobalContextProperty(
          'axon_refresh_token_expiry_ms',
          obtainedInMilliseconds + res.refreshTokenExpiresIn * 1000,
        );
      }
    });
  }

  get settings$(): Observable<SnUserSettings> {
    return this._settings$.asObservable();
  }

  get updated$(): Observable<boolean> {
    return this._updated$.asObservable();
  }

  get callSign$(): Observable<string> {
    return this._callSign$.asObservable();
  }

  get isDeviceLayerOn(): boolean {
    return this.settings.mapSetting.selectedInnerLayers.some((name) => name === 'axonconnect');
  }

  public ngOnDestroy(): void {
    this._settings$.complete();
  }

  public getProfile(): Observable<any> {
    return this._httpClient.get('/api/users/current-user/').pipe(
      tap((response) => {
        this.fromJSON(response);
        this._settings$.next(this.settings);
      }),
      share(),
    );
  }

  public getUser(): Observable<SnUser> {
    if (this.id) {
      return of(this);
    }
    return this.getProfile();
  }

  public updateProfile(data: any): Observable<SnUser> {
    return this._usersAPI.updateUser(this.id, data).pipe(
      tap(this.fromJSON.bind(this)),
      tap(() => {
        this._updated$.next(true);
      }),
    );
  }

  public clear(): void {
    this.fromJSON(new SnUser());
  }

  public getSettings(): Observable<SnUserSettings> {
    return this._httpClient.get(`/api/users/${this.id}/settings/`).pipe(
      map((response) => new SnUserSettings(response)),
      tap((settings) => {
        this.settings = settings;
        this._settings$.next(this.settings);
      }),
      share(),
    );
  }

  public updateSettings(data: any, skipError?: boolean): Observable<SnUserSettings> {
    if (skipError) {
      return this._httpClient
        .patch(`/api/users/${this.id}/settings/`, data, {
          headers: {
            'X-fs-skip-error': 'true',
          },
        })
        .pipe(
          map((response) => new SnUserSettings(response)),
          tap((settings) => {
            this.settings = settings;
            this._settings$.next(this.settings);
          }),
        );
    }
    return this._httpClient.patch(`/api/users/${this.id}/settings/`, data).pipe(
      map((response) => new SnUserSettings(response)),
      tap((settings) => {
        this.settings = settings;
        this._settings$.next(this.settings);
      }),
    );
  }

  public updateCurrentUser(data: any, showMessage?: boolean, message?: string): Observable<SnUser> {
    return this._httpClient.patch('/api/users/current-user/', data).pipe(
      map((response: any) => new SnUser(response)),
      tap({
        next: () => (showMessage && message ? this._toastrService.success(message) : null),
        error: () => this._toastrService.error('Could not update user. Please try again later'),
      }),
    );
  }

  public setUser(user: SnUser): void {
    this.fromJSON(user);
    this._settings$.next(this.settings);
  }

  public callsignChanged(): void {
    this._callSign$.next(this.callSign);
  }

  public getLogData(): {
    email?: string;
    id?: string;
  } {
    return {
      // Email is redacted to prevent sending PII to Datadog
      email: 'REDACTED',
      id: this.id,
    };
  }

  public getUserBoundaries(): Observable<any> {
    return this._httpClient.get(`/api/users/${this.id}/settings/boundaries/`).pipe(
      tap((response) => {
        this.alertBoundaryGeoJson = response?.alertsBoundary;
      }),
    );
  }

  public updateUserBoundaries(data: any): Observable<any> {
    return this._httpClient.patch(`/api/users/${this.id}/settings/boundaries/`, data).pipe(
      tap({
        next: () => {
          this._toastrService.success('Boundary saved successfully');
        },
        error: () => {
          this._toastrService.error('Unable to save boundary. Please try again later');
        },
      }),
    );
  }
}
